import { EditIcon } from '@chakra-ui/icons'
import { Box, Flex, Grid, GridItem, IconButton, Text, Tooltip } from '@chakra-ui/react'
import { formatValue } from '@hb/react'
import { getDateString, Medication, Supplement, WithId } from '@hb/shared'
import { administeredViaField } from '@hb/shared/src/fields/gyn/medicationsAndSupplements'
import { useMemo } from 'react'
import { DeleteButton } from '../Buttons'

export const MedicationPreview = ({
  medication,
  withConditions = false,
  onDelete,
  onEditClick,
}: {
  medication: WithId<Medication>
  withConditions?: boolean
  onDelete: () => Promise<void>
  onEditClick: () => void
}) => (
  <Grid
    bg="#cdcdcd"
    border="1px solid #cdcdcd"
    borderRadius={3}
    templateColumns="repeat(4, minmax(0,1fr))"
    gap="1px"
    w="100%">
    <GridItem colSpan={4}>
      <Flex align="center" bg="white" w="100%">
        <Box px={2} py={1} flex={1}>
          <Text fontSize="lg" fontWeight={500}>
            {medication.name}
          </Text>
        </Box>
        <Flex h="100%" gap={1.5} justify="center" px={2} py={1}>
          <Tooltip
            bg="green.500"
            color="white"
            textShadow="1px 1px 3px #00000055"
            label={`Edit ${medication.name ?? 'Medication'}`}
            placement="top">
            <IconButton
              variant="ghost"
              size="sm"
              icon={<EditIcon color="green.500" />}
              aria-label="Edit"
              onClick={onEditClick}
            />
          </Tooltip>
          <DeleteButton
            variant="ghost"
            size="sm"
            itemName={medication.name ?? 'Medication'}
            onDelete={onDelete}
          />
        </Flex>
      </Flex>
    </GridItem>

    <Box bg="white" px={2} py={1} flex={1}>
      {withConditions ? (
        <Text fontSize="sm" fontFamily="Open Sans">
          For {medication.conditions?.map(condition => condition.name).join(', ')}
        </Text>
      ) : (
        <Text>No reasons for taking provided</Text>
      )}
    </Box>
    <Box bg="white" px={2} py={1} flex={1}>
      <Text>
        {medication.dosage}, {medication.frequency}
      </Text>
    </Box>
    <Box bg="white" px={2} py={1} flex={1}>
      <Text>
        {getDateString(medication.startDate)} -{' '}
        {medication.stopDate ? getDateString(medication.stopDate) : 'Present'}
      </Text>
    </Box>
    <Box bg="white" px={2} py={1} flex={1}>
      <Text>{formatValue({ field: administeredViaField, value: medication.administeredVia })}</Text>
    </Box>
  </Grid>
)

export const SupplementPreview = ({
  supplement,
  withConditions = false,
  onDelete,
  onEditClick,
}: {
  supplement: WithId<Supplement>
  withConditions?: boolean
  onDelete: () => Promise<void>
  onEditClick: () => void
}) => {
  const reasons = useMemo(
    () =>
      [
        supplement.indication,
        ...(supplement.conditions?.map(condition => condition.name) ?? []),
      ].filter(Boolean),
    [supplement],
  )
  return (
    <Grid
      bg="#cdcdcd"
      border="1px solid #cdcdcd"
      borderRadius={3}
      templateColumns="repeat(3, minmax(0,1fr)) auto"
      gap="1px"
      w="100%">
      <Box bg="white" px={2} py={1} flex={1}>
        <Text fontWeight={500}>{supplement.name}</Text>
        {withConditions && reasons.length > 0 ? (
          <Text fontSize="sm" color="gray.500" fontFamily="Open Sans">
            For {reasons.join(', ')}
          </Text>
        ) : null}
      </Box>
      <Box bg="white" px={2} py={1} flex={1}>
        <Text>
          {supplement.dosage}, {supplement.frequency}
        </Text>
      </Box>
      <Box bg="white" px={2} py={1} flex={1}>
        <Text>
          {getDateString(supplement.startDate)} -{' '}
          {supplement.stopDate ? getDateString(supplement.stopDate) : 'Present'}
        </Text>
      </Box>
      <Flex h="100%" bg="gray.100" p={2} gap={2} flexFlow="column" justify="center">
        <IconButton
          variant="outline"
          bg="white"
          size="sm"
          icon={<EditIcon color="green.500" />}
          aria-label="Edit"
          onClick={onEditClick}
        />
        <DeleteButton
          variant="outline"
          bg="white"
          size="sm"
          itemName="Medication"
          onDelete={onDelete}
        />
      </Flex>
    </Grid>
  )
}
